import { useEffect, useRef, useState } from 'react';
import { X, Trash2, Monitor, CheckCheck, MessageSquare, ExternalLink, Loader2, BellOff, UserSearch } from 'lucide-react';
import { useNotificationStore, type NotificationItem } from '@/stores/notification-store';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { openConversationInInbox } from '@/stores/conversation-store';
import { formatTimeAgo, encodeConvIdForHash } from '@/lib/utils/format';

export function NotificationPanel({ position = 'below' }: { position?: 'below' | 'right' } = {}) {
  const panelOpen = useNotificationStore((s) => s.panelOpen);
  const closePanel = useNotificationStore((s) => s.closePanel);
  const notifications = useNotificationStore((s) => s.notifications);
  const notificationsLoading = useNotificationStore((s) => s.notificationsLoading);
  const fetchNotifications = useNotificationStore((s) => s.fetchNotifications);
  const markAsRead = useNotificationStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead);
  const removeNotification = useNotificationStore((s) => s.removeNotification);
  const clearAllNotifications = useNotificationStore((s) => s.clearAllNotifications);
  const enabled = useNotificationStore((s) => s.enabled);
  const requestPermission = useNotificationStore((s) => s.requestPermission);

  const user = useAuthStore((s) => s.user);
  const extensionName = useAppStore((s) => s.extensionName);

  const panelRef = useRef<HTMLDivElement>(null);
  // KAN-188: "Clear all" confirm popup. In-app dialog, not window.confirm —
  // the app runs inside CRM iframes where native dialogs can be blocked.
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);

  // Fetch notifications when panel opens
  // Use impersonatedUserId for admin debugging, but skip selectedAdminUserId
  // (shared users should see their own notifications, not the admin's)
  const impersonatedUserId = useAuthStore((s) => s.impersonatedUserId);
  useEffect(() => {
    const uid = impersonatedUserId ?? user?.uid;
    if (panelOpen && uid && extensionName) {
      fetchNotifications(uid, extensionName);
    }
  }, [panelOpen, user?.uid, impersonatedUserId, extensionName, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    if (!panelOpen || confirmClearOpen) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        // Don't close if clicking the bell itself (it toggles)
        const target = e.target as HTMLElement;
        if (target.closest('[data-notification-bell]')) return;
        closePanel();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [panelOpen, closePanel, confirmClearOpen]);

  if (!panelOpen) return null;

  const userId = impersonatedUserId ?? user?.uid ?? '';
  const extName = extensionName || '';

  const handleMarkAllRead = () => {
    if (userId && extName) markAllAsRead(userId, extName);
  };

  const handleClearAll = async () => {
    if (!userId || !extName) return;
    setClearing(true);
    setClearError(null);
    try {
      await clearAllNotifications(userId, extName);
      setConfirmClearOpen(false);
    } catch (err) {
      console.error('Failed to clear notifications:', err);
      setConfirmClearOpen(false);
      setClearError('Could not clear notifications. Please try again.');
    } finally {
      setClearing(false);
    }
  };

  const handleEnableNotifications = async () => {
    await requestPermission();
  };

  const handleOpenChat = (notif: NotificationItem) => {
    if (!notif.nGrpID) return;
    // Mark the notification row as read (the chat dialog itself will mark the
    // conversation as read via the conversation store).
    if (!notif.readTime && userId && extName) {
      markAsRead(userId, extName, notif.id);
    }
    void openConversationInInbox(notif.nGrpID);
    closePanel();
  };

  const handleNotifClick = (notif: NotificationItem) => {
    if (!notif.readTime && userId && extName) {
      markAsRead(userId, extName, notif.id);
    }
  };

  const handleRemove = (e: React.MouseEvent, notifId: string) => {
    e.stopPropagation();
    if (userId && extName) removeNotification(userId, extName, notifId);
  };

  return (
    <div
      ref={panelRef}
      className={`absolute w-96 max-h-[70vh] flex flex-col rounded-lg border border-border bg-popover shadow-xl z-50 overflow-hidden ${
        position === 'right'
          ? 'left-full bottom-0 ml-1'
          : 'right-0 top-full mt-1'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-base font-semibold text-foreground">Notifications</h3>
        <button
          onClick={closePanel}
          className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/30">
        {!enabled && (
          <button
            onClick={handleEnableNotifications}
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          >
            <Monitor className="h-3.5 w-3.5" />
            Enable Now
          </button>
        )}
        <button
          onClick={handleMarkAllRead}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
        >
          <CheckCheck className="h-3.5 w-3.5" />
          Mark all as read
        </button>
        {notifications.length > 0 && (
          <button
            onClick={() => { setClearError(null); setConfirmClearOpen(true); }}
            data-testid="notification-clear-all"
            className="ml-auto inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Clear all
          </button>
        )}
      </div>
      {clearError && (
        <p className="px-4 py-2 text-xs text-destructive border-b border-border">{clearError}</p>
      )}
      <ConfirmDialog
        open={confirmClearOpen}
        onClose={() => { if (!clearing) setConfirmClearOpen(false); }}
        onConfirm={handleClearAll}
        title="Clear all notifications?"
        description="This deletes all your notifications. This cannot be undone."
        confirmText="Clear all"
        variant="destructive"
        loading={clearing}
      />

      {/* Notification list */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {notificationsLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <BellOff className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No notifications yet</p>
          </div>
        ) : (
          notifications.map((notif) => {
            const isUnread = !notif.readTime;
            return (
              <div
                key={notif.id}
                onClick={() => handleNotifClick(notif)}
                className={`relative px-4 py-3 border-b border-border/50 cursor-pointer transition-colors hover:bg-muted/50 ${
                  isUnread ? 'bg-primary/5' : ''
                }`}
              >
                {/* Unread indicator */}
                {isUnread && (
                  <div className="absolute left-1.5 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-primary" />
                )}

                {/* Delete button */}
                <button
                  onClick={(e) => handleRemove(e, notif.id)}
                  className="absolute right-3 top-3 rounded p-1 text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
                  title="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>

                {/* Title */}
                <p className={`text-sm pr-6 ${isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground/80'}`}>
                  {notif.nTtl}
                </p>

                {/* Message body */}
                <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                  {notif.nMsg}
                </p>

                {/* Footer: actions + time */}
                <div className="flex items-center gap-3 mt-2">
                  {/* Open Record — resolves CRM record via notification-click handler */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const url = `${window.location.origin}/app-notification-click?notificationId=${notif.id}&appCode=${extName}&notificationTitle=${encodeURIComponent(notif.nTtl)}&notificationContent=${encodeURIComponent(notif.nMsg)}`;
                      window.open(url, '_blank');
                    }}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-amber-600 transition-colors"
                    title="Open Record"
                  >
                    <UserSearch className="h-3.5 w-3.5" />
                  </button>
                  {notif.nGrpID && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenChat(notif);
                      }}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                      title="Open Chat"
                    >
                      <MessageSquare className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {notif.nGrpID && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = `${window.location.origin}/${extName}/inbox#${encodeConvIdForHash(notif.nGrpID!)}`;
                        window.open(url, '_blank');
                      }}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                      title="Open in New Tab"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <span className="ml-auto text-[11px] text-muted-foreground">
                    {formatTimeAgo(notif.ct)}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
